import classNames from 'classnames';
import React, {useMemo} from 'react';

import {useI18n} from '@udemy/i18n';
import {StepCard, type StepCardProps} from '@udemy/react-certification-preparation';

import {CertificationVoucherProviderType} from 'src/generated/graphql';

import {trackClickExamLink} from '../../events';
import styles from './description.module.less';
import {
    QuestionAnswerItem,
    type QuestionAnswer as QuestionAnswerType,
} from './question-answer.react-component';

export interface DescriptionProps {
    examTitle: string;
    examId: string;
    examProviderType: CertificationVoucherProviderType;
    poolId: string;
    questionAnswers: QuestionAnswerType[];
    discountPercentage: string;
    hasDiscount?: boolean;
    isUdemyIssuedRetake?: boolean;
}
const STEP_CARD_STANDARD_VALUES = {
    iconColor: 'color-red-100' as StepCardProps['iconColor'],
    position: 'bottom' as StepCardProps['position'],
};
export const Description: React.FC<DescriptionProps> = ({
    questionAnswers,
    discountPercentage,
    hasDiscount = false,
    examTitle,
    examId,
    examProviderType,
    poolId,
    isUdemyIssuedRetake = false,
}) => {
    const {gettext, interpolate} = useI18n();

    // TODO: CER-437: Delete this as part of the cleanup where the bullet points will stored in the backend.
    const isLinuxFoundationPage = useMemo(
        () => examProviderType === CertificationVoucherProviderType.LinuxFoundation,
        [examProviderType],
    );

    const handleContainerClick = (e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;

        const linkElement = target.closest('a');

        if (linkElement) {
            trackClickExamLink({
                name: examTitle,
                examId: examId,
                poolId: poolId,
                voucherId: null,
            });
        }
    };

    const firstStepDescription = isUdemyIssuedRetake
        ? gettext('Get exam-ready with expert-led Udemy courses and practice tests.')
        : gettext('Get ready for your exam with expert-led Udemy courses and practice tests');

    const secondStepTitle = isUdemyIssuedRetake
        ? gettext('Buy your voucher with Second Chance')
        : gettext('Purchase exam voucher');

    const secondStepDescription = isUdemyIssuedRetake
        ? gettext('Purchase through Udemy and lock in a second attempt at a discounted price.')
        : hasDiscount
          ? interpolate(
                gettext(
                    'Purchase your exam voucher through Udemy and save %(discountPercentage)s%',
                ),
                {discountPercentage},
                true,
            )
          : gettext('Purchase your exam voucher through Udemy');

    const thirdStepTitle = isUdemyIssuedRetake
        ? gettext('Book your exam and take it')
        : gettext('Schedule and take your exam');

    const thirdStepDescription = isUdemyIssuedRetake
        ? gettext(
              "Use your voucher code to schedule your exam. If you don't pass, just let us know and we'll take care of the rest.",
          )
        : !isLinuxFoundationPage
          ? gettext('Use your voucher code when scheduling your exam')
          : gettext(
                "Use your voucher code when scheduling your exam, see 'How do I use my voucher below'.",
            );

    return (
        <div className={classNames(styles['container'])}>
            <div className={classNames(styles['centered'], 'ud-container')}>
                {isUdemyIssuedRetake && (
                    <span className={classNames(styles['second-chance-title'], 'ud-heading-xxl')}>
                        {gettext('How Second Chance works')}
                    </span>
                )}
                <div className={styles['steps']}>
                    <StepCard
                        number={1}
                        title={gettext('Learn and practice')}
                        description={firstStepDescription}
                        {...STEP_CARD_STANDARD_VALUES}
                    />
                    <StepCard
                        number={2}
                        title={secondStepTitle}
                        description={secondStepDescription}
                        {...STEP_CARD_STANDARD_VALUES}
                    />
                    <StepCard
                        number={3}
                        title={thirdStepTitle}
                        description={thirdStepDescription}
                        {...STEP_CARD_STANDARD_VALUES}
                    />
                </div>
                {questionAnswers.map((questionAnswer, index) => (
                    <QuestionAnswerItem
                        key={index}
                        questionAnswer={questionAnswer}
                        index={index}
                        handleContainerClick={handleContainerClick}
                    />
                ))}
            </div>
        </div>
    );
};
